import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminCreateUserAttributeCategoryMutation,
  InternalAdminCreateUserAttributeCategoryMutationVariables,
  InternalAdminCreateUserAttributeMutation,
  InternalAdminCreateUserAttributeMutationVariables,
  InternalAdminTagCategoryFragment,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import InternalAdminTagCategorySelect from "../components/internal-admin-tag-category-select";
import useInternalAdminUserAttributeCategoryList from "../hooks/use-internal-admin-user-attribute-category-list";
import InternalAdminCreateUserAttributeSelect, {
  INewUserAttributeOption,
} from "../components/internal-admin-create-user-attribute-select";
import useQueryString from "modules/hooks/use-query-string";
import { getMessageFromError } from "modules/utils/Functions";
import INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_MUTATION from "../_networking/internal-admin-create-user-attribute";
import INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_CATEGORY_MUTATION from "../_networking/internal-admin-create-user-attribute-category";

interface IProps {
  onSaveCallback?: Function;
}

const InternalAdminAddUserAttributeCategoryModal = ({
  onSaveCallback,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const { closeModal } = useOverlay();
  const [value, setValue] = useState<readonly INewUserAttributeOption[]>([]);
  const [tagCategory, setTagCategory] =
    useState<InternalAdminTagCategoryFragment | null>();

  const { userAttributeCategories } = useInternalAdminUserAttributeCategoryList(
    {
      organizationId: qsOrganizationId,
      input: {
        limit: 1000000,
        offset: 0,
      },
    },
  );

  const excludeTagCategoryIds = useMemo(
    () =>
      userAttributeCategories
        .map((userAttributeCategory) => userAttributeCategory.tagCategoryId)
        .filter((id): id is string => Boolean(id)),
    [userAttributeCategories],
  );

  const [internalAdminCreateUserAttributeCategoryMutationAPI, { loading }] =
    useMutation<
      InternalAdminCreateUserAttributeCategoryMutation,
      InternalAdminCreateUserAttributeCategoryMutationVariables
    >(INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_CATEGORY_MUTATION);

  const [internalAdminCreateUserAttributeMutationAPI] = useMutation<
    InternalAdminCreateUserAttributeMutation,
    InternalAdminCreateUserAttributeMutationVariables
  >(INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_MUTATION);

  const duplicateUserAttributes = useMemo(
    () =>
      value
        .filter((v) => !!v.duplicateUserAttribute)
        .map((v) => v.duplicateUserAttribute!),
    [value],
  );

  const hasDuplicate = duplicateUserAttributes.length > 0;

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      if (!qsOrganizationId) return;
      internalAdminCreateUserAttributeCategoryMutationAPI({
        variables: {
          organizationId: qsOrganizationId,
          input: {
            name,
            ...(tagCategory?.id ? { tagCategoryId: tagCategory.id } : {}),
          },
        },
        onError: (error: Error) => {
          setError(SERVER_ERROR, {
            message: getMessageFromError(error.message),
          });
        },
        onCompleted: async (data) => {
          const userAttributeCategory =
            data?.internalAdminCreateUserAttributeCategory;
          if (!userAttributeCategory) {
            return;
          }
          if (value.length === 0) {
            closeModal();
            return;
          }
          await Promise.all(
            value.map((name) =>
              internalAdminCreateUserAttributeMutationAPI({
                variables: {
                  organizationId: qsOrganizationId,
                  input: {
                    userAttributeCategoryId: userAttributeCategory.id,
                    name: name.value,
                  },
                },
              }),
            ),
          );
          if (onSaveCallback) {
            onSaveCallback();
          }
          closeModal();
        },
      });
    },
    [
      closeModal,
      internalAdminCreateUserAttributeCategoryMutationAPI,
      internalAdminCreateUserAttributeMutationAPI,
      onSaveCallback,
      qsOrganizationId,
      setError,
      tagCategory,
      value,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
              Add User Attributes
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form
            id="admin-add-user-attribute-category-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack alignItems="flex-start" spacing={6}>
              <FormControl isInvalid={!!errors?.name}>
                <VStack w="full" alignItems="flex-start" spacing={1}>
                  <Text
                    fontSize={16}
                    fontWeight={700}
                    color={colors.blackSecondary}
                  >
                    Attribute Category Name
                  </Text>
                  <Input
                    id="name"
                    placeholder="Create unique name"
                    _placeholder={{
                      color: colors.placeholderText,
                      fontStyle: "italic",
                    }}
                    {...register("name", {
                      required: {
                        message: "a name is required",
                        value: true,
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.name?.message)}
                  </FormErrorMessage>
                </VStack>
              </FormControl>
              <FormControl>
                <Text
                  fontSize={16}
                  fontWeight={700}
                  color={colors.blackSecondary}
                >
                  Use an existing tag category and tags (optional)
                </Text>
                <InternalAdminTagCategorySelect
                  onChange={setTagCategory}
                  excludeTagCategoryIds={excludeTagCategoryIds}
                />
              </FormControl>
              {!tagCategory && (
                <FormControl>
                  <VStack w="full" spacing={2} alignItems="flex-start">
                    <Text
                      fontSize={16}
                      fontWeight={700}
                      color={colors.blackSecondary}
                    >
                      Attributes
                    </Text>
                    <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                      Type words and add a comma or hit return to set attribute
                    </Text>
                    <InternalAdminCreateUserAttributeSelect
                      onChange={setValue}
                    />
                  </VStack>
                </FormControl>
              )}
            </Stack>
            {hasDuplicate && (
              <Text color={colors.red}>Duplicate attributes found</Text>
            )}
            {errors.SERVER_ERROR && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.SERVER_ERROR?.message)}
              </Text>
            )}
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                  isDisabled={hasDuplicate}
                >
                  {loading ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminAddUserAttributeCategoryModal);
